import React, { useEffect, useState } from 'react';
import ReactJson from '@microlink/react-json-view';
import FormatSwitch, { DataFormat } from '@src/ui/FormatSwitch';
import YamlView from '@src/ui/YamlView';
import './JsonPanel.css';

/**
 * A JSON document, read-only, in a frame.
 *
 * This is the viewer from the service item's Declarations tab — the one the
 * rest of the app shows a declaration in — said once instead of being restated
 * with slightly different props everywhere a document turns up. The frame is
 * the panel's; the viewer inside it draws no border of its own.
 *
 * With `enableYaml` the reader can switch it to YAML and back, from a switch
 * in the frame's corner; `format` is the view it opens on.
 */
const JsonPanel = ({
    data,
    /** extra classes on the frame, for a page that already styles this box */
    className,
    /** collapse below this depth — leave off to open the whole document */
    collapsed,
    /** the root name; off by default, since the frame already says what this is */
    name = false,
    /** a JSON / YAML switch for the reader; off by default */
    enableYaml = false,
    /** the view it opens on, with the switch: 'json' (default) or 'yaml' */
    format = 'json',
}: {
    data: any;
    className?: string;
    collapsed?: boolean | number;
    name?: string | false;
    enableYaml?: boolean;
    format?: DataFormat;
}) => {
    const [shown, setShown] = useState<DataFormat>(format);
    useEffect(() => setShown(format), [format]);
    const asYaml = enableYaml && shown === 'yaml';

    return (
        <div className={`njp${enableYaml ? ' njp--switch' : ''}${className ? ` ${className}` : ''}`}>
            {enableYaml && (
                <div className="njp__switch">
                    <FormatSwitch value={shown} onChange={setShown} />
                </div>
            )}
            {asYaml ? (
                <YamlView data={data} className="njp__yaml" />
            ) : (
                <ReactJson
                    style={{ padding: '16px', background: 'transparent' }}
                    src={data}
                    name={name}
                    collapsed={collapsed}
                    displayDataTypes={false}
                    displayArrayKey={false}
                    displayObjectSize={false}
                />
            )}
        </div>
    );
};

export default JsonPanel;
